/**
 * Backend error codes → Russian user-facing messages. Plan §5 surface codes
 * plus transport-layer synthetic codes (offline, timeout) used by the client.
 *
 * Keep this in sync with backend/internal/handler/*.go respondError calls —
 * when adding a new code on the server, add a translation here.
 *
 * We store pairs as a tuple array and build the lookup map at module load —
 * keeps Biome's useNamingConvention rule happy (snake_case keys otherwise
 * trigger a lint error for every code).
 */
type Pair = readonly [code: string, message: string]

const PAIRS: readonly Pair[] = [
  // Auth / access (plan §5.1, §8)
  ['unauthenticated', 'Нужно войти заново.'],
  ['invalid_credentials', 'Неверный логин или пароль.'],
  ['forbidden', 'Нет доступа к этому разделу.'],
  ['cannot_target_self', 'Действие над собственным аккаунтом недоступно.'],
  ['csrf_required', 'Сессия устарела. Обновите страницу.'],

  // Generic / validation
  ['internal_error', 'На сервере что-то пошло не так. Попробуйте ещё раз.'],
  ['validation_error', 'Проверьте поля формы и попробуйте ещё раз.'],
  ['invalid_id', 'Некорректный идентификатор.'],
  ['invalid_period', 'Некорректный период.'],
  ['invalid_role', 'Неизвестная роль пользователя.'],
  ['invalid_status', 'Некорректный статус.'],
  ['invalid_consultation_date', 'Некорректная дата консультации.'],
  ['not_found', 'Запись не найдена.'],
  ['handler_error', 'Не удалось обработать запрос. Попробуйте ещё раз.'],

  // Upload (plan §5.2)
  ['audio_missing', 'Прикрепите аудиофайл.'],
  ['audio_empty', 'Файл пустой — выберите другой.'],
  ['invalid_format', 'Поддерживаются mp3, m4a, mp4 и wav.'],
  ['transcode_failed', 'Не удалось обработать этот файл — выберите другой.'],
  ['file_too_large', 'Файл больше 200 МБ — уменьшите запись.'],
  ['duration_too_short', 'Запись короче 30 секунд.'],
  ['duration_too_long', 'Запись длиннее 90 минут.'],
  ['duplicate', 'Такая запись уже загружена ранее.'],
  ['rate_limit_exceeded', 'Сегодня вы уже загрузили 20 записей — лимит на сутки.'],
  ['no_store', 'К аккаунту не привязан магазин. Обратитесь к администратору.'],

  // Admin (plan §5.4)
  ['login_taken', 'Такой логин уже занят.'],
  ['seller_store_limit', 'Продавца можно привязать только к одному магазину.'],
  ['not_failed', 'Перезапуск доступен только для записей со статусом «Ошибка».'],

  // Pipeline surface (admin sees these on failed consultations)
  ['stt_failed', 'Не удалось расшифровать аудио.'],
  ['stt_timeout', 'Расшифровка заняла слишком много времени.'],
  ['permanent_error', 'Обработка завершилась с ошибкой и не будет повторена.'],

  // Transport layer (synthesized client-side)
  ['network_error', 'Проверьте интернет и попробуйте ещё раз.'],
  ['timeout', 'Сервер не отвечает. Попробуйте ещё раз.'],
  ['offline', 'Нет интернета. Проверьте соединение.'],
  ['unknown_error', 'Что-то пошло не так. Попробуйте ещё раз.'],
]

export const ERROR_MESSAGES: Readonly<Record<string, string>> = Object.fromEntries(PAIRS)

const FALLBACK = 'Что-то пошло не так. Попробуйте ещё раз.'

/** Typed accessor for synthetic (transport-layer) codes. */
export function synthetic(code: 'network_error' | 'timeout' | 'offline' | 'unknown_error'): string {
  return ERROR_MESSAGES[code] ?? FALLBACK
}

/** Look up a backend error code; falls back to caller-provided text, then
 * to a generic "something went wrong". */
export function messageForCode(code: string, fallback?: string): string {
  return ERROR_MESSAGES[code] ?? fallback ?? FALLBACK
}
